import { SortOptionEnum } from 'src/components/price-list/types';
import { AlternativePrice, Price } from 'src/types';
import { IListPrices } from '../types';

type IFilterFunc = (offer: Price) => boolean;

const getHasCertainData: IFilterFunc = (p: Price) => !p.uncertainData;

const getHasShippingPriceInfo: IFilterFunc = (p: Price) =>
  Boolean(p.price?.inclShipping);

const getHasFeaturedStoreInfo: IFilterFunc = (p: Price) =>
  Boolean(p.store?.featured);

const getHasVariantInfo: IFilterFunc = (p: Price) => {
  const hasVariantInfo = p.variantInfo?.size?.length;
  const hasAlternativeVariantInfo = p.alternativePrices?.some(
    (a?: AlternativePrice | null) => a?.variantInfo?.size?.length
  );
  return Boolean(hasVariantInfo || hasAlternativeVariantInfo);
};

const createFilters = (
  currentSorting: SortOptionEnum,
  isPriceIncShipping: boolean,
  hasVariantsSelections: boolean
): IFilterFunc[] => {
  const filters: IFilterFunc[] = [];
  switch (currentSorting) {
    case SortOptionEnum.RECOMMENDED:
    case SortOptionEnum.PRICEANDSTOCKSTATUS:
      filters.push(getHasCertainData);
      break;
    case SortOptionEnum.FEATURED:
      filters.push(getHasFeaturedStoreInfo);
      break;
    default:
      break;
  }
  if (hasVariantsSelections) {
    filters.push(getHasVariantInfo);
  }
  if (isPriceIncShipping) {
    filters.push(getHasShippingPriceInfo);
  }
  return filters;
};

const splitOfferlistUsingFilters = (
  offerlist: Price[] | null | undefined,
  filters: IFilterFunc[]
): IListPrices => {
  if (!offerlist?.length || !filters.length) {
    return {
      mainList: offerlist || [],
      secondaryList: [],
    };
  }
  const splittedList: IListPrices = {
    mainList: [],
    secondaryList: [],
  };
  offerlist.forEach(offer => {
    if (filters.every(filterFn => filterFn(offer))) {
      splittedList.mainList.push(offer);
    } else {
      splittedList.secondaryList!.push(offer);
    }
  });
  return splittedList;
};

export const getSplitOfferlist = (
  offerlist: Price[] | null | undefined,
  currentSorting: SortOptionEnum,
  isPriceIncShipping: boolean,
  hasVariantsSelections: boolean
): IListPrices => {
  const filters = createFilters(
    currentSorting,
    isPriceIncShipping,
    hasVariantsSelections
  );
  return splitOfferlistUsingFilters(offerlist, filters);
};

export const TOTAL_OFFERS_LIMIT = 15;
export const MAIN_LIST_DEFAULT_LIMIT = 10;
export const SECOND_LIST_DEFAULT_LIMIT =
  TOTAL_OFFERS_LIMIT - MAIN_LIST_DEFAULT_LIMIT;

export const getListLimits = (prices: IListPrices) => {
  const maxMainOffers = Math.min(prices.mainList.length, TOTAL_OFFERS_LIMIT);
  const maxSecondOffers = Math.min(
    prices.secondaryList.length,
    TOTAL_OFFERS_LIMIT
  );
  const maxMainOffersWhenSecondaryListIsLimitedByDefault =
    maxSecondOffers > SECOND_LIST_DEFAULT_LIMIT
      ? MAIN_LIST_DEFAULT_LIMIT
      : TOTAL_OFFERS_LIMIT - maxSecondOffers;
  const mainListLimit = Math.min(
    maxMainOffers,
    maxMainOffersWhenSecondaryListIsLimitedByDefault
  );
  const secondaryListLimit = TOTAL_OFFERS_LIMIT - mainListLimit;
  return {
    mainListLimit,
    secondaryListLimit,
  };
};
